import { MetadataSymbol } from "core/Misc/decorators.functions.js";
/**
 * Enum defining the type of properties that can be edited in the property pages in the node editor
 */
export var PropertyTypeForEdition;
(function (PropertyTypeForEdition) {
    /** property is a boolean */
    PropertyTypeForEdition[PropertyTypeForEdition["Boolean"] = 0] = "Boolean";
    /** property is a float */
    PropertyTypeForEdition[PropertyTypeForEdition["Float"] = 1] = "Float";
    /** property is a int */
    PropertyTypeForEdition[PropertyTypeForEdition["Int"] = 2] = "Int";
    /** property is a Vector2 */
    PropertyTypeForEdition[PropertyTypeForEdition["Vector2"] = 3] = "Vector2";
    /** property is a list of values */
    PropertyTypeForEdition[PropertyTypeForEdition["List"] = 4] = "List";
})(PropertyTypeForEdition || (PropertyTypeForEdition = {}));
/** @internal */
// eslint-disable-next-line @typescript-eslint/naming-convention
const __bjsSmartFilterPropStoreKey = "__bjs_sf_prop_store__";
/**
 * Decorator that flags a property in a node block as being editable
 * @param displayName - the display name of the property
 * @param propertyType - the type of the property
 * @param groupName - the group name of the property
 * @param options - the options of the property
 * @returns the decorator
 */
export function EditableInPropertyPage(displayName, propertyType = PropertyTypeForEdition.Boolean, groupName = "PROPERTIES", options) {
    return (_value, context) => {
        const meta = context.metadata;
        if (!meta) {
            // `context.metadata` is `void 0` when `Symbol.metadata` was not installed before this class
            // was evaluated. Importing `MetadataSymbol` from core runs the polyfill at module load (before
            // this class body), so this should never happen; referencing it here also keeps that
            // module-load polyfill anchored against bundler tree-shaking.
            throw new Error(`EditableInPropertyPage: decorator metadata is unavailable; the Symbol.metadata (${String(MetadataSymbol)}) polyfill must run before decorated classes are evaluated.`);
        }
        let propStore;
        if (Object.prototype.hasOwnProperty.call(meta, __bjsSmartFilterPropStoreKey)) {
            propStore = meta[__bjsSmartFilterPropStoreKey];
        }
        else {
            propStore = [];
            meta[__bjsSmartFilterPropStoreKey] = propStore;
        }
        const propertyKey = String(context.name);
        const propToAdd = {
            propertyName: propertyKey,
            displayName: displayName,
            type: propertyType,
            groupName: groupName,
            options: options ?? {},
            className: "",
        };
        // If the property already exists, overwrite it, otherwise add it
        // Note: It may have been redefined since the application started
        const existingIndex = propStore.findIndex((p) => p.propertyName === propertyKey && options?.blockType === p.options?.blockType);
        if (existingIndex !== -1) {
            propStore[existingIndex] = propToAdd;
        }
        else {
            propStore.push(propToAdd);
        }
    };
}
/**
 * Gets the editable properties for a given target using TC39 decorator metadata.
 * Walks the metadata prototype chain to include properties from parent classes.
 * @param target - the target object (instance or constructor)
 * @returns array of property descriptions
 */
export function GetSmartFilterEditableProperties(target) {
    const ctor = typeof target === "function" ? target : target?.constructor;
    const metadata = ctor?.[MetadataSymbol];
    if (!metadata) {
        return [];
    }
    const result = [];
    let currentMeta = metadata;
    while (currentMeta) {
        if (Object.prototype.hasOwnProperty.call(currentMeta, __bjsSmartFilterPropStoreKey)) {
            const store = currentMeta[__bjsSmartFilterPropStoreKey];
            result.push(...store);
        }
        currentMeta = Object.getPrototypeOf(currentMeta);
    }
    return result;
}
//# sourceMappingURL=editableInPropertyPage.js.map